import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
} from "@mui/material";
import { Letter, Hostage } from "../types";
import "../../style/LetterCard.css";
import { useTranslation } from "react-i18next";
import { Share, Delete, Edit, Reply } from "@mui/icons-material";
import { useUser } from "../../context/UserContext";
import LetterForm from "./LetterForm";
import userIcon from "../../icons/hostages.svg";
import Linkify from "react-linkify";
import { HostagePageLink } from "../HostagePageLink";
import Flag from "react-world-flags";
import countries from "i18n-iso-countries";


type Props = {
  letter: Letter;
  hostage: Hostage;
  onRefresh?: () => void;
  onDeleteLetter?: (id: string | undefined) => void;
};

const LetterCard = ({ letter, hostage, onRefresh, onDeleteLetter }: Props) => {
  const { t, i18n } = useTranslation();
  const { user } = useUser();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isAddLetterDrawerOpen, setIsAddLetterDrawerOpen] = useState(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [isReply, setIsReply] = useState(false);

  const maxTextLength = 300;

  const handleToggleReadMore = () => {
    setIsDescriptionExpanded(!isDescriptionExpanded);
  };

  const truncatedText =
    letter?.text?.length > maxTextLength && !isDescriptionExpanded
      ? `${letter.text.substring(0, maxTextLength)}...`
      : letter.text;

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          text: t("eventCard.shareText", { letterName: letter.text }),
          url: window.location.href,
        });
      } catch (err) {
        console.error("Error sharing:", err);
      }
    }
  };

  const confirmDelete = () => {
    fetch(`/api/letter/${letter.id}`, {
      method: "DELETE",
      credentials: "include",
      headers: {
        "X-HOSTAGE-ID": letter.hostage,
      },
    })
      .catch((err) => {
        console.error("Error deleting event:", err);
      })
      .finally(() => {
        setIsDeleteDialogOpen(false); // Close the dialog after deletion
        if (onDeleteLetter) {
          onDeleteLetter(letter.id);
        }
        if (onRefresh) {
          onRefresh();
        }
      });
  };

  function canEdit() {
    return (
      letter.authorEmail === user?.email || isAdmin());
  }

  function isAdmin() {
    return !!(
      user?.role === "admin" ||
      (user?.role === "moderator" && letter.hostage && user?.moderating?.includes(letter.hostage))
    );
  }

  function handleEditLetter(): void {
    setIsAddLetterDrawerOpen(true);
  }

  function handleReplyLetter(): void {
    setIsReply(true);
    setIsAddLetterDrawerOpen(true);
  }

  function getShareTimeString(): string {
    if (!letter.createdAt) {
      return "";
    }
    const createdTime = new Date(letter.createdAt);
    const now = new Date();
    const diffInMs = now.getTime() - createdTime.getTime();

    const diffInSeconds = Math.floor(diffInMs / 1000);
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);
    const diffInWeeks = Math.floor(diffInDays / 7);

    if (diffInMinutes < 1) {
      return t("letter.timeAgo.justNow");
    } else if (diffInMinutes < 60) {
      return t("letter.timeAgo.minutesAgo", { count: diffInMinutes });
    } else if (diffInHours < 24) {
      return t("letter.timeAgo.hoursAgo", { count: diffInHours });
    } else if (diffInDays < 7) {
      return t("letter.timeAgo.daysAgo", { count: diffInDays });
    } else {
      return t("letter.timeAgo.weeksAgo", { count: diffInWeeks });
    }
  }

  function getCountryName(code: string | undefined): string {
    if (!code) {
      return "";
    }
    return countries.getName(code, i18n.language) || "Unknown Country";
  };

  return (
    <Card
      className={`letter-container 
        ${letter.isAdmin ? 'admin-background' : ''} 
        ${letter.replyToId ? 'letter-reply' : ''}`}
    >
      <Box display="flex" alignItems="center" sx={{ padding: "8px" }}>
        <Box display="flex" alignItems="center" padding="0 5px">
          <img
            src={userIcon}
            alt="user"
            style={{ width: "32px", height: "32px", borderRadius: "50%", objectFit: "cover"}}
          />
        </Box>
        <Typography variant="body2">
        </Typography>
        <Box display="flex" flexDirection="column" padding="0 5px" width="100%">
          <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
            <Typography variant="body2" style={{ flex: 1 }}>
              {letter.author ? letter.author : t("letter.anonymous")}
            </Typography>
            <Flag
              code={letter.authorCountry ?? ""}
              alt={`Flag of ${letter.authorCountry}`}
              style={{ width: "20px", height: "auto", margin: "0 10px" }}
            />
          </Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
            <Typography variant="body2" style={{ lineHeight: "1", color: "#727272", flex: 1 }}>
              {getShareTimeString()}
            </Typography>
            <Typography
              variant="body2"
              style={{ lineHeight: "1", color: "#727272" }}
            >
              {getCountryName(letter.authorCountry)}
            </Typography>
          </Box>
        </Box>
      </Box>
      <CardContent className="letter-card-content">
        <Typography variant="body2" align="right" whiteSpace={"pre-line"}>
          <Linkify>{truncatedText}</Linkify>
        </Typography>
        {letter?.text?.length > maxTextLength && (
          <Button onClick={handleToggleReadMore} className="read-more">
            {isDescriptionExpanded ? t("letter.readLess") : t("letter.readMore")}
          </Button>
        )}
      </CardContent>
      {letter.photo && (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: 0,
            padding: 0,
          }}
        >
          <img
            src={letter.photo}
            alt="story"
            style={{
              width: "100%",
              height: "auto",
              display: "block",
            }}
          />
        </Box>
      )}
      <Box
        className="share-edit-delete-container"
        display="flex"
        justifyContent="flex-start"
        mt={2}
      >
        <IconButton onClick={handleShare}>
          <Share />
        </IconButton>
        {canEdit() && (
          <IconButton aria-label="edit" onClick={() => handleEditLetter()}>
            <Edit />
          </IconButton>
        )}
        {canEdit() && (
          <IconButton aria-label="delete" onClick={() => setIsDeleteDialogOpen(true)}>
            <Delete />
          </IconButton>
        )}
        {isAdmin() && (letter.replyToId == undefined) && (
          <IconButton aria-label="reply" onClick={() => handleReplyLetter()}>
            <Reply />
          </IconButton>
        )}
      </Box>
      <LetterForm
        open={isAddLetterDrawerOpen}
        onClose={() => {
          setIsAddLetterDrawerOpen(false);
          window.location.reload();
        }}
        hostageId={letter.hostage}
        isAdmin={isAdmin()}
        letter={isReply ? undefined : letter}
        replyToId={isReply ? letter.id : undefined}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{t("letter.deleteConfirmTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {t("letter.deleteConfirmText")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteDialogOpen(false)} color="primary">
            {t("letter.cancelBtn")}
          </Button>
          <Button onClick={confirmDelete} color="secondary" autoFocus>
            {t("letter.confirmBtn")}
          </Button>
        </DialogActions>
      </Dialog>
      {letter.hostageName && letter.hostage ? (
        <HostagePageLink hostageId={letter.hostage} hostageName={letter.hostageName} />
      ) : null}
    </Card>
  );
};

export default LetterCard;
